import { useRouter } from 'next/router';
import { getWindow, trackEvent } from '@surfline/web-common';

import ContentCarousel from 'components/ContentCarousel';
import OverlayCard from 'components/OverlayCard';
import type { OverlayCardProps, OverlayCardData } from 'types/cards';
import type { TravelZonePlaylistType } from 'types/contentful/content-types';
import { zoneCardTransformer } from 'utils/transformers';

const zonePlaylistCard = (
  itemData: OverlayCardData,
  cardProps: Omit<OverlayCardProps, 'data'>,
  router: { asPath?: string },
  screenConfig: { small: number; medium: number; mobile: number },
  carouselTitle?: string,
  pageTitle?: string,
  index?: number,
  carouselRef?: React.RefObject<HTMLDivElement>,
) => (
  <OverlayCard
    {...cardProps}
    data={itemData}
    screenConfig={screenConfig}
    carouselRef={carouselRef}
    trackCardClick={() => {
      trackEvent('Carousel Clicked', {
        carouselItem: itemData?.title,
        carouselName: `${
          pageTitle === 'travel homepage'
            ? `landing page playlist ${carouselTitle}`
            : 'Explore Other Zones'
        }`,
        category: 'travel',
        path: router.asPath,
        positionNumber: index,
        referrer: `${getWindow()?.location?.href ?? ''}${router.asPath}`,
        search: getWindow().location.search,
        title: pageTitle,
        url: `${getWindow()?.location?.href ?? ''}${router.asPath}`,
      });
    }}
  />
);

type Props = {
  data: TravelZonePlaylistType;
  pageTitle?: string;
};

const TravelZonePlaylist: React.FC<Props> = ({ data, pageTitle }) => {
  const { asPath } = useRouter();

  const DEFAULT_COLORS = {
    bg: 'white',
    skeletonBg: '#eaeaea',
    skeletonItem: '#f7f7f7',
    headerText: '#171717',
    headerButton: '#171717',
  };

  const screenConfig = { small: 2, mobile: 1, medium: data?.playlistColumns || 3 };

  return (
    <section id="explore-other-zones">
      <ContentCarousel
        carouselItem={(itemData, index, carouselRef) =>
          zonePlaylistCard(
            {
              ...zoneCardTransformer(itemData),
              link: `/travel/zones/${itemData?.slug}/${itemData?.sys?.id}`,
            },
            {
              showSubtitle: !!data?.showCardSubtitles,
              showCaption: !!data?.showCardCaptions,
            },
            { asPath },
            screenConfig,
            data?.title || '',
            pageTitle,
            index,
            carouselRef,
          )
        }
        colors={data?.playlistColors || DEFAULT_COLORS}
        dataTest="zone-playlist-section"
        minItemWidth="84%"
        itemAspectRatio={data?.itemAspectRatio || 3 / 4}
        numberOfitems={data?.playlistColumns || 3}
        rawData={data?.zonesCollection?.items}
        screenConfig={screenConfig}
        showPageCount={data?.showPageCount || true}
        subtitle={data?.subtitle || ''}
        title={data?.title || ''}
        useSLSectionPadding
      />
    </section>
  );
};

export default TravelZonePlaylist;
